// SPS-5005 Meetings Hub — client-scoped service.
//
// Talks to the planned RO backend endpoints (another agent is building them):
//   GET /clients/{clientId}/v1/advisor
//   GET /clients/{clientId}/v1/meetings?scope=upcoming|past
//   GET /clients/{clientId}/v1/meetings/{id}
//   GET /clients/{clientId}/v1/meetings/{id}/transcript
//
// Request.get prepends constant.BACKEND_DOMAIN and attaches the bearer token,
// same as every other service (see SimpleDashboard / PerformanceDashboard).
//
// DEV-FIXTURES fallback: if a call throws or comes back empty, we fall back to
// the local meetingsFixtures module so the Meetings page renders end-to-end
// before the backend ships. Set the fixtures flag off once the API is live.

import Request from './Request'
import {
  type Meeting,
  type MeetingAdvisor,
  type MeetingTranscriptLine,
  getFixtureAdvisor,
  getFixtureMeeting,
  getFixtureMeetings,
  getFixtureTranscript,
} from '../pages/dashboards/v3/components/meetingsFixtures'

export type MeetingScope = 'upcoming' | 'past'

// One row from GET /api/client-dashboard-notifications/calendar. The CTA/register link is
// embedded in `body` (there is no separate URL field).
export interface CalendarEvent {
  id: number | string
  title: string
  body?: string | null
  event_date: string // 'YYYY-MM-DD'
  client_id?: number | null
  is_global?: boolean
  status?: string // 'active' | 'draft' | 'inactive'
  release_at?: string | null
  expire_at?: string | null
  sort_order?: number
}

export interface GetMeetingsOptions {
  scope?: MeetingScope
  [key: string]: any
}

// Synthetic demo fixtures as a LAST-RESORT fallback, OFF by default so real clients
// never see demo data when the backend returns empty/errors. A developer can opt in
// locally with REACT_APP_MEETINGS_FIXTURES=true to preview the page without a live
// backend (e.g. before the branch is deployed to dev).
const FIXTURES_FALLBACK_ENABLED = process.env.REACT_APP_MEETINGS_FIXTURES === 'true'

function encodeSegment(value: string | number): string {
  return encodeURIComponent(String(value ?? ''))
}

// A payload may arrive as the object itself or wrapped in { data: ... }.
function unwrap<T>(response: any): T | null {
  if (response === null || typeof response === 'undefined') return null
  if (response && typeof response === 'object' && 'data' in response && response.data !== undefined) {
    return response.data as T
  }
  return response as T
}

function isEmpty(value: any): boolean {
  if (value === null || typeof value === 'undefined') return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

export default class MeetingsService {
  // `clientCode` scopes the optional synthetic-fixtures fallback (off by default) to the
  // current client's recordings. Not sent to the backend (the client is scoped by id).
  static async getAdvisor(clientId: string | number, clientCode?: string): Promise<MeetingAdvisor | null> {
    try {
      const response = await Request.get(`/api/clients/${encodeSegment(clientId)}/v1/advisor`)
      const advisor = unwrap<MeetingAdvisor>(response)
      if (!isEmpty(advisor)) return advisor as MeetingAdvisor
    } catch (error) {
      console.warn('MeetingsService.getAdvisor falling back to fixtures:', error)
    }

    return FIXTURES_FALLBACK_ENABLED ? getFixtureAdvisor(clientCode) : null
  }

  static async getMeetings(
    clientId: string | number,
    { scope = 'upcoming', clientCode, ...rest }: GetMeetingsOptions = {},
  ): Promise<Meeting[]> {
    try {
      const response = await Request.get(`/api/clients/${encodeSegment(clientId)}/v1/meetings`, {
        scope,
        ...rest,
      })
      const meetings = unwrap<Meeting[]>(response)
      if (!isEmpty(meetings) && Array.isArray(meetings)) return meetings as Meeting[]
    } catch (error) {
      console.warn('MeetingsService.getMeetings falling back to fixtures:', error)
    }

    return FIXTURES_FALLBACK_ENABLED ? getFixtureMeetings(scope, clientCode) : []
  }

  // Calendar M1 Events (green markers) — client-scoped (global + this client), INCLUDING
  // past/out-of-window and non-active (draft/inactive) events. The register/CTA link lives
  // inside each event's `body`. Returns [] on error so the calendar still renders.
  static async getCalendarEvents(clientId: string | number): Promise<CalendarEvent[]> {
    try {
      const response = await Request.get('/api/client-dashboard-notifications/calendar', {
        client_id: clientId,
      })
      const events = unwrap<CalendarEvent[]>(response)
      if (Array.isArray(events)) return events as CalendarEvent[]
    } catch (error) {
      console.warn('MeetingsService.getCalendarEvents failed:', error)
    }
    return []
  }

  // Per-client HubSpot scheduling page URL, used for the "Schedule call" / "Open HubSpot
  // calendar" / "Reschedule" embeds. Returns null when the client has no scheduling URL
  // configured (callers then show a "not configured" state).
  static async getHubspotCalendarUrl(clientId: string | number): Promise<string | null> {
    try {
      const response = await Request.get(`/api/clients/${encodeSegment(clientId)}/v1/hubspot-calendar`)
      const payload = unwrap<{ schedulingUrl?: string | null }>(response)
      const url = payload && typeof payload === 'object' ? payload.schedulingUrl : null
      if (typeof url === 'string' && url.length > 0) return url
    } catch (error) {
      console.warn('MeetingsService.getHubspotCalendarUrl failed:', error)
    }
    return null
  }

  static async getMeeting(clientId: string | number, id: string, clientCode?: string): Promise<Meeting | null> {
    try {
      const response = await Request.get(`/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}`)
      const meeting = unwrap<Meeting>(response)
      if (!isEmpty(meeting)) return meeting as Meeting
    } catch (error) {
      console.warn('MeetingsService.getMeeting falling back to fixtures:', error)
    }

    return FIXTURES_FALLBACK_ENABLED ? getFixtureMeeting(id, clientCode) : null
  }

  static async getTranscript(clientId: string | number, id: string, clientCode?: string): Promise<MeetingTranscriptLine[]> {
    try {
      const response = await Request.get(
        `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/transcript`,
      )
      const transcript = unwrap<MeetingTranscriptLine[]>(response)
      if (!isEmpty(transcript) && Array.isArray(transcript)) return transcript as MeetingTranscriptLine[]
    } catch (error) {
      console.warn('MeetingsService.getTranscript falling back to fixtures:', error)
    }

    return FIXTURES_FALLBACK_ENABLED ? getFixtureTranscript(id, clientCode) : []
  }

  // Media-proxy: resolve a fresh signed recording URL for in-page playback.
  // The backend re-verifies client ownership before any Grain call and returns a
  // short-lived signed URL (self-authorizing), so the browser can <video src> it
  // directly. Falls back to the local fixture's videoUrl for the dev preview.
  static async getRecordingUrl(clientId: string | number, id: string, clientCode?: string): Promise<string | null> {
    try {
      const response = await Request.get(
        `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/recording`,
      )
      const payload = unwrap<{ url?: string }>(response)
      const url = payload && typeof payload === 'object' ? payload.url : undefined
      if (typeof url === 'string' && url.length > 0) return url
    } catch (error) {
      console.warn('MeetingsService.getRecordingUrl falling back to fixtures:', error)
    }

    return FIXTURES_FALLBACK_ENABLED ? getFixtureMeeting(id, clientCode)?.videoUrl ?? null : null
  }

  // ---- app-state writes (per-user, persisted server-side) -------------------
  // All fire-and-forget: the page applies the change optimistically, so if the
  // backend isn't reachable (e.g. the endpoint isn't deployed yet) the UI is
  // unaffected and the error is swallowed. They persist once the backend ships.

  static async setStar(clientId: string | number, id: string, starred: boolean): Promise<void> {
    const path = `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/star`
    try {
      if (starred) await Request.post(path, {})
      else await Request._delete(path)
    } catch (error) {
      console.warn('MeetingsService.setStar failed (optimistic UI retained):', error)
    }
  }

  static async setHidden(clientId: string | number, id: string, hidden: boolean): Promise<void> {
    const path = `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/hide`
    try {
      if (hidden) await Request.post(path, {})
      else await Request._delete(path)
    } catch (error) {
      console.warn('MeetingsService.setHidden failed (optimistic UI retained):', error)
    }
  }

  static async saveNote(clientId: string | number, id: string, body: string): Promise<void> {
    try {
      await Request.post(`/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/notes`, { body })
    } catch (error) {
      console.warn('MeetingsService.saveNote failed:', error)
    }
  }

  static async markViewed(clientId: string | number, id: string): Promise<void> {
    try {
      await Request.post(`/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/viewed`, {})
    } catch (error) {
      /* best-effort: clears the "New" badge on next load */
    }
  }

  static async setActionState(
    clientId: string | number,
    id: string,
    actionKey: string,
    action: 'accept' | 'dismiss',
  ): Promise<void> {
    try {
      await Request.post(
        `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/actions/${encodeSegment(actionKey)}/${action}`,
        {},
      )
    } catch (error) {
      console.warn('MeetingsService.setActionState failed:', error)
    }
  }

  static async setActionDone(
    clientId: string | number,
    id: string,
    actionKey: string,
    done: boolean,
  ): Promise<void> {
    try {
      await Request.post(
        `/api/clients/${encodeSegment(clientId)}/v1/meetings/${encodeSegment(id)}/actions/${encodeSegment(actionKey)}/done`,
        { done },
      )
    } catch (error) {
      console.warn('MeetingsService.setActionDone failed:', error)
    }
  }
}
